import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  soalKestabilan, getSoalKestabilan, hitungSkorKestabilan,
  getLabelDimensi, getKesimpulanKestabilan,
  DIMENSI_KESTABILAN, URUTAN_DIMENSI_KESTABILAN,
} from './bankSoalKestabilan';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes, ambilSoalDariId } from './useResumeTes';

const WAKTU_TOTAL = 70 * 60; // 70 menit untuk 565 butir Ya/Tidak (~7.4 detik/butir)

function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// ─── Banner lanjutkan progres tersimpan ────────────────────────
function BannerLanjutkan({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const totalSoal = progresTersimpan.soalIds?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-sky-400 bg-sky-50">
      <p className="text-xs font-bold uppercase tracking-wide text-sky-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-sky-800 mb-3">
        Baru sampai butir <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-sky-300 text-sky-600 text-sm font-semibold hover:bg-sky-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Intro ────────────────────────────────────────────────
function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🛡️</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Kestabilan Pribadi</h1>
          <p className="text-gray-400 text-sm mt-1">
            {tryoutMode ? 'Bagian dari Tryout Resmi' : 'Kuesioner Ya/Tidak — latihan pengenalan diri'}
          </p>
        </div>

        <BannerLanjutkan progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
          <p className="text-xs text-amber-700 font-semibold mb-1">⚠️ Petunjuk Penting</p>
          <ul className="text-xs text-amber-600 space-y-1">
            <li>• Jawab <b>Ya</b> atau <b>Tidak</b> sejujur mungkin, tidak ada jawaban benar/salah</li>
            <li>• {soalKestabilan.length} pernyataan singkat, jawab spontan tanpa terlalu lama berpikir</li>
            <li>• Timer {WAKTU_TOTAL / 60} menit berjalan otomatis, progres tersimpan otomatis</li>
            <li>• Hasil berupa gambaran kecenderungan diri, <b>bukan diagnosis klinis/kejiwaan</b></li>
          </ul>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-gray-800">{soalKestabilan.length}</p><p className="text-xs text-gray-500">Butir</p></div>
          <div><p className="text-lg font-bold text-gray-800">{WAKTU_TOTAL / 60}'</p><p className="text-xs text-gray-500">Waktu</p></div>
          <div><p className="text-lg font-bold text-gray-800">8</p><p className="text-xs text-gray-500">Dimensi</p></div>
        </div>

        <div className="rounded-xl p-4 mb-5 bg-sky-50 border border-sky-100">
          <p className="text-xs font-bold text-sky-700 mb-2">Dimensi yang diukur:</p>
          <div className="grid grid-cols-2 gap-1.5">
            {URUTAN_DIMENSI_KESTABILAN.map(d => (
              <div key={d} className="flex items-center gap-1.5 text-xs text-sky-800">
                <span>{DIMENSI_KESTABILAN[d].icon}</span>
                <span>{DIMENSI_KESTABILAN[d].nama}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          {!tryoutMode && (
            <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Kembali</button>
          )}
          <button onClick={onMulai}
            className="flex-grow py-3 rounded-xl font-bold border-2 border-sky-400 bg-sky-50 text-sky-700 hover:opacity-90 transition">
            Mulai Sekarang →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Layar Hasil ────────────────────────────────────────────────
function LayarHasil({ soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const hasil = hitungSkorKestabilan(soalList, jawabanUser);
  const kesimpulan = getKesimpulanKestabilan(hasil.dimensiPct);

  useEffect(() => {
    catatHasilTes('kestabilan', hasil.skorKeseluruhan, { pctSelesai: hasil.pctSelesai, dimensiPct: hasil.dimensiPct }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">

        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">🛡️</div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Hasil Kestabilan Pribadi</h2>
          <p className="text-xs text-gray-400 mb-4">Gambaran kecenderungan hasil latihan pengenalan diri</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-2xl font-extrabold text-gray-800">{hasil.totalDijawab}</p>
              <p className="text-xs text-gray-500">Butir Dijawab</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-2xl font-extrabold text-gray-800">{hasil.totalSoal - hasil.totalDijawab}</p>
              <p className="text-xs text-gray-500">Tidak Dijawab</p>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div className="bg-sky-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${hasil.pctSelesai}%` }} />
          </div>
          <p className="text-xs text-gray-400">{hasil.pctSelesai}% butir terjawab</p>
        </div>

        {/* Rincian per dimensi */}
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm font-bold text-sky-700 mb-3">📊 Rincian per Dimensi</p>
          {URUTAN_DIMENSI_KESTABILAN.map(d => {
            const pct = hasil.dimensiPct[d];
            const info = DIMENSI_KESTABILAN[d];
            const labelInfo = getLabelDimensi(d, pct);
            return (
              <div key={d} className="mb-4 last:mb-0">
                <div className="flex justify-between items-start text-xs mb-1">
                  <span className="text-gray-700 font-semibold">{info.icon} {info.nama}</span>
                  <span className="font-bold" style={{ color: labelInfo.warna }}>{pct === null ? '—' : `${pct}%`}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${pct || 0}%`, backgroundColor: labelInfo.warna }} />
                </div>
                <p className="text-[11px] text-gray-500">{labelInfo.label}</p>
              </div>
            );
          })}
        </div>

        {/* Kesimpulan deskriptif (non-klinis) */}
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm font-bold text-sky-700 mb-3">📝 Ringkasan Kecenderungan</p>

          {kesimpulan.kekuatan.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-green-700 mb-1">Aspek yang tampak menonjol:</p>
              <ul className="text-xs text-gray-600 space-y-0.5">
                {kesimpulan.kekuatan.map(k => (
                  <li key={k.id}>• {DIMENSI_KESTABILAN[k.id].icon} {k.nama} ({k.pct}%)</li>
                ))}
              </ul>
            </div>
          )}

          {kesimpulan.perhatian.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">Aspek yang bisa terus dilatih:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                {kesimpulan.perhatian.map((p, i) => (
                  <li key={p.id}>
                    • {DIMENSI_KESTABILAN[p.id].icon} {p.nama} ({p.pct}%){kesimpulan.saran[i] ? ` — ${kesimpulan.saran[i]}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-lg p-3 bg-amber-50 border border-amber-200 mt-3">
            <p className="text-[11px] text-amber-700 leading-relaxed">⚠️ {kesimpulan.disclaimer}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4 flex gap-3">
          <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-sky-600 text-white font-bold hover:bg-sky-700 transition">🔄 Ulangi</button>
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Menu</button>
        </div>
      </div>
    </div>
  );
}

// ─── Komponen Utama ─────────────────────────────────────────────
export default function KestabilanModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]               = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [soalList, setSoalList]       = useState([]);
  const [indeks, setIndeks]           = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa]     = useState(WAKTU_TOTAL);
  const timerRef                      = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('kestabilan', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'kestabilan', tryoutId, {
    soalIds: soalList.map(s => s.id),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  const handleMulai = useCallback(() => {
    hapusProgresTes('kestabilan', tryoutId);
    const soal = getSoalKestabilan();
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(WAKTU_TOTAL);
    setProgresTersimpan(null);
    setFase('tes');
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = ambilSoalDariId(soalKestabilan, progresTersimpan.soalIds || []);
    if (soal.length === 0) { setProgresTersimpan(null); return; }
    setSoalList(soal);
    setIndeks(Math.min(progresTersimpan.indeks || 0, soal.length - 1));
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(progresTersimpan.waktuSisa ?? WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('kestabilan', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); hapusProgresTes('kestabilan', tryoutId); setFase('hasil'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  const handleJawab = useCallback((nilai) => {
    setJawabanUser(prev => prev.map(j => j.id === soalList[indeks].id ? { ...j, jawaban: nilai } : j));
    // Otomatis maju ke butir berikutnya biar pengerjaan 565 butir tetap cepat.
    setIndeks(i => (i < soalList.length - 1 ? i + 1 : i));
  }, [indeks, soalList]);

  const handlePrev = useCallback(() => { if (indeks > 0) setIndeks(i => i - 1); }, [indeks]);
  const handleNext = useCallback(() => { if (indeks < soalList.length - 1) setIndeks(i => i + 1); }, [indeks, soalList.length]);

  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('kestabilan', tryoutId);
    setFase('hasil');
  }, [tryoutId]);

  const handleUlang = useCallback(() => {
    setFase('intro'); setSoalList([]); setIndeks(0);
    setJawabanUser([]); setWaktuSisa(WAKTU_TOTAL);
  }, []);

  if (fase === 'intro') {
    return (
      <LayarIntro
        onMulai={handleMulai}
        onBack={onBack}
        tryoutMode={tryoutMode}
        progresTersimpan={progresTersimpan}
        onLanjutkan={handleLanjutkan}
        onMulaiBaru={handleMulaiBaruDariBanner}
      />
    );
  }
  if (fase === 'hasil') return <LayarHasil tryoutMode={tryoutMode} tryoutId={tryoutId} soalList={soalList} jawabanUser={jawabanUser} onUlang={handleUlang} onBack={onBack} />;

  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id)?.jawaban;
  const progress        = ((indeks + 1) / soalList.length) * 100;
  const dijawab          = jawabanUser.filter(j => j.jawaban === 'ya' || j.jawaban === 'tidak').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg
              ${waktuSisa < 300 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              <span className="text-sky-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <div className="text-right">
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-sky-100 text-sky-700">
                {DIMENSI_KESTABILAN[soal.dimensi]?.icon} {DIMENSI_KESTABILAN[soal.dimensi]?.nama}
              </span>
              <p className="text-[10px] text-gray-400 mt-0.5">{dijawab} dijawab</p>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-sky-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          <p className="text-base font-semibold text-gray-800 leading-relaxed text-center py-6">{soal.pernyataan}</p>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => handleJawab('ya')}
              className={`py-4 rounded-xl border-2 font-bold text-sm transition
                ${jawabanSoalIni === 'ya' ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-gray-200 bg-white text-gray-600 hover:border-sky-300 hover:bg-sky-50'}`}>
              ✓ Ya
            </button>
            <button onClick={() => handleJawab('tidak')}
              className={`py-4 rounded-xl border-2 font-bold text-sm transition
                ${jawabanSoalIni === 'tidak' ? 'border-gray-500 bg-gray-100 text-gray-800' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}>
              ✗ Tidak
            </button>
          </div>
        </div>

        <div className="flex gap-3 mb-4">
          <button onClick={handlePrev} disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-sky-600 text-white font-bold hover:bg-sky-700 transition">
              Lewati →
            </button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">
              Selesai ✓
            </button>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-2">
          Jawaban tersimpan otomatis, tekan Ya/Tidak untuk lanjut ke butir berikutnya
        </p>
      </div>
    </div>
  );
}
